import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { copyTextToClipboard } from "src/domains/shared/utils/copyTextToClipboard";
import notice from "src/domains/shared/utils/notice";
import { sharePage } from "./sharePage";
import { SITE_HEADER_STYLES as S } from "./siteHeaderStyles";

/** 복사한 뒤 버튼이 체크 아이콘 + 「링크 복사」 로 보이는 시간 */
const COPIED_FEEDBACK_MS = 2000;

/** 공유 아이콘(토스와 같은 세 점을 선으로 이은 모양, 24 상자) */
const SHARE_NODE_RADIUS = 3;
const SHARE_NODES: readonly (readonly [number, number])[] = [
  [18, 5],
  [6, 12],
  [18, 19],
];
const SHARE_LINKS_PATH = "M8.7 10.6l6.6-4.2M8.7 13.4l6.6 4.2";
const CHECK_PATH = "M5 12.5l4.5 4.5L19 7.5";

const ICON_SVG_PROPS = {
  viewBox: "0 0 24 24",
  "aria-hidden": true,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

function ShareIcon() {
  return (
    <svg {...ICON_SVG_PROPS}>
      {SHARE_NODES.map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={SHARE_NODE_RADIUS} />
      ))}
      <path d={SHARE_LINKS_PATH} />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...ICON_SVG_PROPS}>
      <path d={CHECK_PATH} />
    </svg>
  );
}

/** 복사 직후 잠깐 「링크 복사」 로 보이는 상태 — 다시 누르면 시간이 처음부터 */
function useCopiedFeedback(): [copied: boolean, markCopied: () => void] {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  useEffect(() => () => clearTimeout(timerRef.current), []);
  const markCopied = () => {
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };
  return [copied, markCopied];
}

/**
 * 넓은 화면 머리말의 「공유」(marketing-site-shell 「머리말 구성」) — 아이콘(장식)과 그 오른쪽 글자. 누르면 기기 공유 창이나
 * 알림 없이 지금 주소를 복사하고, 버튼이 잠깐 체크 아이콘 + 「링크 복사」 로 바뀐다. 복사가 안 될 때만 오류 알림.
 */
export function SharePageButton() {
  const { t } = useTranslation("landing");
  const [copied, markCopied] = useCopiedFeedback();

  const copyLink = async () => {
    if (await copyTextToClipboard(window.location.href)) markCopied();
    else notice("error", t("site.share.failed"));
  };

  return (
    <Box
      component="button"
      type="button"
      data-copied={copied ? "" : undefined}
      sx={S.share}
      onClick={() => {
        void copyLink();
      }}
    >
      {copied ? <CheckIcon /> : <ShareIcon />}
      <span aria-live="polite">
        {t(copied ? "site.share.copied" : "site.share.label")}
      </span>
    </Box>
  );
}

/**
 * 좁은 화면 메뉴 목록의 「공유하기」(marketing-site-shell 「좁은 화면 머리말과 모바일 메뉴」) — 로그인 상태 항목 바로 위.
 * 기기 공유 창(Web Share)을 열고, 없으면 주소를 복사해 항목이 잠깐 체크 아이콘 + 「링크 복사」 로 바뀐다. 취소는 조용히.
 */
export function ShareMenuItem() {
  const { t } = useTranslation("landing");
  const [copied, markCopied] = useCopiedFeedback();

  const share = async () => {
    const outcome = await sharePage({
      title: document.title,
      url: window.location.href,
    });
    if (outcome === "copied") markCopied();
    else if (outcome === "failed") notice("error", t("site.share.failed"));
  };

  return (
    <button
      type="button"
      data-share=""
      data-copied={copied ? "" : undefined}
      onClick={() => {
        void share();
      }}
    >
      {copied ? <CheckIcon /> : null}
      <span aria-live="polite">
        {t(copied ? "site.share.copied" : "site.share.menuLabel")}
      </span>
    </button>
  );
}
